import { Eye } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useKindList } from '@/api/queries/catalog-queries'
import { useCustomerClassifications } from '@/api/queries/customer-classifications-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { usePeople } from '@/api/queries/people-queries'
import { useCreateVereAgir } from '@/api/queries/ver-e-agir-queries'
import { Form } from '@/components/forms/form'
import { FormField } from '@/components/forms/form-field'
import { FormItem } from '@/components/forms/form-item'
import {
	INSPECTION_COLORS,
	InspectionCard,
	InspectionCardFooter,
	InspectionCardHeader,
	InspectionDateField,
	InspectionOptionGroup,
	InspectionPhotoField,
} from '@/components/inspections'
import { CardContent } from '@/components/ui/card'
import { Field, FieldContent } from '@/components/ui/field'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import { ROUTES } from '@/constants/routes'
import { useFormErrorFocus } from '@/hooks/forms/use-form-error-focus'
import { useVereAgirForm } from '@/hooks/forms/use-vere-agir-form'
import type { VereAgirFormData } from '@/schemas/ver-e-agir-schema'
import { useProfileStore } from '@/store'
import type { InspectionStatus, VereAgirSituation } from '@/types'
import {
	getApiErrorMessage,
	isPayloadTooLargeApiError,
} from '@/utils/api-error'
import { convertHeicIfNeeded } from '@/utils/image-compression'

const colors = INSPECTION_COLORS.grassGreen

export const SeeAndActPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()
	const form = useVereAgirForm()
	const {
		setFocus,
		formState: { errors },
	} = form

	const { handleError } = useFormErrorFocus(errors, setFocus)

	const kindId = form.watch('kindId')
	const image = form.watch('image')

	const { defaultBranchId, defaultContractId, defaultCustomerId } =
		useProfileStore()
	const { data: branchOptions } = useOrganization()

	// Context fields (persisted in zustand)
	const branchId = defaultBranchId
	const crId = defaultContractId

	const selectedBranch = branchOptions?.find((b) => b.id === branchId)
	const branchName = selectedBranch?.name || ''

	const { data: employeeData } = usePeople()

	// Specific fields
	const [photoPreview, setPhotoPreview] = useState<string | null>(null)

	const { mutateAsync, isPending } = useCreateVereAgir()
	const { data: kindData } = useKindList()
	const { data: classificationData } = useCustomerClassifications({
		page: 1,
		limit: 100,
	})
	const selectedKind = kindData?.items.find((k) => k.id === kindId)

	const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = e.target.files?.[0]
		if (!picked) return
		const file = await convertHeicIfNeeded(picked)
		form.setValue('image', file, {
			shouldDirty: true,
			shouldValidate: true,
		})
		setPhotoPreview(URL.createObjectURL(file))
	}

	const handleRemovePhoto = () => {
		form.setValue('image', null, {
			shouldDirty: true,
			shouldValidate: true,
		})
		if (photoPreview) URL.revokeObjectURL(photoPreview)
		setPhotoPreview(null)
	}

	const handleSubmit = async (formData: VereAgirFormData) => {
		if (
			!formData.date ||
			!formData.kindId ||
			!formData.riskId ||
			!formData.description ||
			!formData.immediateAction ||
			!formData.responsibleId ||
			!formData.dueDate ||
			!formData.status ||
			!formData.situation
		) {
			toast.error(t('inspections.seeAndAct.validationError'))
			return
		}
		const selectedCr = selectedBranch?.crs?.find((c) => c.id === crId)

		// require customer when CR has customers
		if ((selectedCr?.customers?.length ?? 0) > 0 && !defaultCustomerId) {
			toast.error(
				t('inspections.common.selectCustomerRequired') ||
					'Selecione um cliente',
			)
			return
		}

		try {
			await mutateAsync({
				crId,
				...(defaultCustomerId ? { customerId: defaultCustomerId } : {}),
				...(formData.customerClassificationId
					? { customerClassificationId: formData.customerClassificationId }
					: {}),
				date: formData.date,
				kindId: formData.kindId,
				riskId: formData.riskId,
				description: formData.description,
				immediateAction: formData.immediateAction,
				responsibleId: formData.responsibleId,
				dueDate: formData.dueDate,
				status: formData.status as InspectionStatus,
				situation: formData.situation as VereAgirSituation,
				image: image ?? undefined,
			})
			toast.success(t('inspections.seeAndAct.saveSuccess'))
			navigate(ROUTES.INSPECTIONS.SEE_AND_ACT.INDEX)
		} catch (error) {
			toast.error(
				isPayloadTooLargeApiError(error)
					? t('errors.payload_too_large')
					: getApiErrorMessage(error),
			)
		}
	}

	const statusOptions = [
		{
			value: 'IN_PROGRESS',
			label: t('inspections.seeAndAct.statusInProgress'),
		},
		{ value: 'RESOLVED', label: t('inspections.seeAndAct.statusResolved') },
	]

	const situationOptions = [
		{
			value: 'STOP_AND_FIX' as VereAgirSituation,
			label: t('inspections.seeAndAct.situationStopAndFix'),
		},
		{
			value: 'FIX_AND_CONTINUE' as VereAgirSituation,
			label: t('inspections.seeAndAct.situationFixAndContinue'),
		},
	]

	return (
		<InspectionCard colors={colors}>
			<InspectionCardHeader
				icon={<Eye className="size-5" />}
				title={t('inspections.types.seeAndAct.title')}
				colors={colors}
				onBack={() => navigate(ROUTES.INSPECTIONS.SEE_AND_ACT.INDEX)}
				branch={branchName}
				branchId={branchId}
				branchList={branchOptions}
				crId={crId}
				crList={selectedBranch?.crs}
			/>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit, handleError)}
					className="flex flex-1 flex-col"
				>
					<CardContent className="flex flex-col gap-6 py-5">
						{/* ── Context fields ── */}
						<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
							<FormField
								name="date"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.common.date')}
										error={form.formState.errors.date?.message}
									>
										<InspectionDateField
											value={field.value ? new Date(field.value) : undefined}
											onChange={(date) =>
												field.onChange(date ? date.toISOString() : '')
											}
										/>
									</FormItem>
								)}
							/>

							<FormField
								name="kindId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.seeAndAct.typology')}
										error={form.formState.errors.kindId?.message}
									>
										<FieldContent fieldName={field.name}>
											<NativeSelect
												name={field.name}
												ref={field.ref}
												value={field.value}
												onChange={(e) => {
													field.onChange(e)
													form.setValue('riskId', '')
												}}
												className="w-full min-w-40 truncate"
											>
												<NativeSelectOption value="" disabled>
													{t('inspections.seeAndAct.selectTypology')}
												</NativeSelectOption>
												{kindData?.items.map((item) => (
													<NativeSelectOption
														key={item.id}
														value={item.id}
														disabled={(item.risks ?? []).length === 0}
													>
														{item.name}
													</NativeSelectOption>
												))}
											</NativeSelect>
										</FieldContent>
									</FormItem>
								)}
							/>

							<FormField
								name="riskId"
								control={form.control}
								render={({ field }) => (
									<FormItem
										label={t('inspections.seeAndAct.risk')}
										error={form.formState.errors.riskId?.message}
									>
										<Field className="animate-in fade-in slide-in-from-top-2 duration-200">
											<FieldContent fieldName={field.name}>
												<NativeSelect
													name={field.name}
													ref={field.ref}
													value={field.value}
													onChange={field.onChange}
													className="w-full"
													disabled={(selectedKind?.risks ?? []).length === 0}
												>
													<NativeSelectOption value="" disabled>
														{t('inspections.seeAndAct.selectRisk')}
													</NativeSelectOption>
													{(selectedKind?.risks ?? []).map((item) => (
														<NativeSelectOption key={item.id} value={item.id}>
															{item.name}
														</NativeSelectOption>
													))}
												</NativeSelect>
											</FieldContent>
										</Field>
									</FormItem>
								)}
							/>
						</div>

						<FormField
							name="customerClassificationId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.customerClassification')}
									isRequired={false}
									error=""
								>
									<FieldContent fieldName={field.name}>
										<NativeSelect
											name={field.name}
											ref={field.ref}
											value={field.value ?? ''}
											onChange={field.onChange}
											className="w-full truncate"
										>
											<NativeSelectOption value="">
												{t(
													'inspections.seeAndAct.selectCustomerClassification',
												)}
											</NativeSelectOption>
											{classificationData?.items.map((item) => (
												<NativeSelectOption key={item.id} value={item.id}>
													{item.name}
												</NativeSelectOption>
											))}
										</NativeSelect>
									</FieldContent>
								</FormItem>
							)}
						/>

						<Separator className="bg-border/60" />

						<FormField
							name="description"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.description')}
									error={form.formState.errors.description?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t(
												'inspections.seeAndAct.descriptionPlaceholder',
											)}
											rows={4}
											className="resize-y"
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="immediateAction"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.immediateAction')}
									error={form.formState.errors.immediateAction?.message}
								>
									<FieldContent fieldName={field.name}>
										<Textarea
											name={field.name}
											ref={field.ref}
											value={field.value}
											onChange={field.onChange}
											placeholder={t(
												'inspections.seeAndAct.immediateActionPlaceholder',
											)}
											rows={4}
											className="resize-y"
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<Separator className="bg-border/60" />

						<FormField
							name="responsibleId"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.responsible')}
									error={form.formState.errors.responsibleId?.message}
								>
									<FieldContent>
										<SearchableSelect
											ref={field.ref}
											value={field.value}
											nameField={field.name}
											onValueChange={field.onChange}
											options={
												employeeData?.map((p) => ({
													value: p.id,
													label: p.name,
												})) ?? []
											}
											placeholder={t('inspections.seeAndAct.selectResponsible')}
											searchPlaceholder={t('common.search')}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						{/* ── Action plan ── */}
						<FormField
							name="dueDate"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.deadline')}
									error={form.formState.errors.dueDate?.message}
								>
									<InspectionDateField
										value={field.value ? new Date(field.value) : undefined}
										onChange={(date) =>
											field.onChange(date ? date.toISOString() : '')
										}
										isDueDate={true}
									/>
								</FormItem>
							)}
						/>

						<FormField
							name="status"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.status')}
									error={form.formState.errors.status?.message}
								>
									<FieldContent fieldName={field.name}>
										<InspectionOptionGroup
											value={field.value}
											onChange={field.onChange}
											options={statusOptions}
											activeClass={colors.optionActive}
											columns={2}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="situation"
							control={form.control}
							render={({ field }) => (
								<FormItem
									label={t('inspections.seeAndAct.situation')}
									error={form.formState.errors.situation?.message}
								>
									<FieldContent fieldName={field.name}>
										<InspectionOptionGroup
											value={field.value}
											onChange={field.onChange}
											options={situationOptions}
											activeClass={colors.optionActive}
											columns={2}
										/>
									</FieldContent>
								</FormItem>
							)}
						/>

						<FormField
							name="image"
							control={form.control}
							render={() => (
								<FormItem
									label={t('inspections.seeAndAct.photo')}
									isRequired={false}
									error=""
								>
									<InspectionPhotoField
										attachLabel={t('inspections.seeAndAct.attachPhoto')}
										altText={t('inspections.seeAndAct.photoSelected')}
										preview={photoPreview}
										onFileChange={handlePhotoChange}
										onRemove={handleRemovePhoto}
										colors={colors}
									/>
								</FormItem>
							)}
						/>
					</CardContent>

					<InspectionCardFooter
						colors={colors}
						isSubmitting={isPending}
						onCancel={() => navigate(ROUTES.INSPECTIONS.SEE_AND_ACT.INDEX)}
						cancelLabel={t('inspections.seeAndAct.cancel')}
						saveLabel={t('inspections.seeAndAct.save')}
						savingLabel={t('inspections.seeAndAct.saving')}
					/>
				</form>
			</Form>
		</InspectionCard>
	)
}
